import React, { useEffect } from 'react';

const Modal = ({ isOpen, onClose, service }) => {
  // Fermer le modal avec la touche Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Empêcher le scroll du body
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !service) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-yellow-400 text-white p-8 rounded-t-3xl">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-6xl">{service.icon}</div>
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold mb-2">{service.title}</h2>
                <p className="text-white/90 text-lg">{service.subtitle}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              aria-label="Fermer"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 space-y-8">
          {/* Description */}
          <div>
            <h3 className="text-2xl font-bold mb-4 text-gray-800">Description</h3>
            <p className="text-gray-600 text-lg leading-relaxed">{service.description}</p>
          </div>

          {/* Services Inclus */}
          <div>
            <h3 className="text-2xl font-bold mb-4 text-gray-800">Services Inclus</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {service.detailedItems.map((item, index) => (
                <div key={index} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-xl">
                  <div className="flex-shrink-0 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">{item.title}</h4>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tarifs */}
          <div>
            <h3 className="text-2xl font-bold mb-4 text-gray-800">Tarifs Indicatifs</h3>
            <div className="bg-gradient-to-br from-blue-50 to-orange-50 p-6 rounded-xl">
              <div className="space-y-3">
                {service.pricing.map((price, index) => (
                  <div key={index} className="flex justify-between items-center border-b border-gray-200 last:border-0 pb-3 last:pb-0">
                    <span className="text-gray-700 font-medium">{price.service}</span>
                    <span className="text-orange-600 font-bold text-lg">{price.price}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-4 italic">* Devis gratuit et personnalisé sur demande</p>
            </div>
          </div>

          {/* Avantages */}
          <div>
            <h3 className="text-2xl font-bold mb-4 text-gray-800">Pourquoi Nous Choisir ?</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {service.advantages.map((advantage, index) => (
                <div key={index} className="text-center p-6 bg-white border-2 border-gray-100 rounded-xl hover:border-orange-300 transition-colors">
                  <div className="text-4xl mb-3">{advantage.icon}</div>
                  <h4 className="font-bold text-gray-800 mb-2">{advantage.title}</h4>
                  <p className="text-gray-600 text-sm">{advantage.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-orange-500 to-yellow-400 p-8 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Prêt à démarrer votre projet ?</h3>
            <p className="mb-6 text-white/90">Contactez-nous pour un devis gratuit et personnalisé</p>
            <button 
              onClick={() => {
                onClose();
                const element = document.getElementById('contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="bg-white text-orange-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors inline-flex items-center space-x-2"
            >
              <span>Demander un devis</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;